import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Dropdown, FormGroup, FormLabel, Row } from "react-bootstrap";

import ListPage from "../../../layouts/page/ListPage";
import PageTitle from "../../../layouts/PageTitle";
import FilterAccordion from "../../../components/Common/Filter/FilterAccordion";
import EmployeeSelect from "../../../components/Custom/Main/Employee/EmployeeSelect";
import InputDate from "../../../components/Forms/Element/InputDate";
import InputText from "../../../components/Forms/Element/InputText";
import CustomSelect from "../../../components/Forms/Element/CustomSelect";
import ListMetricCard, { ListMetricCards } from "../../../components/Common/ListParts/ListMetricCard";
import ModalPageEvent from "../../../components/Common/Modal/ModalPageEvent";
import AsteriskRequired from "../../../components/Common/AsteriskRequired";
import { ApiError } from "../../../components/Common/ApiError";
import { successAlert, warnAlert } from "../../../components/alert/defaultAlert";
import SaveIcon from "../../../components/Icon/SaveIcon";
import PaymentMethodSelect from "../../../components/Custom/Common/PaymentMethod/PaymentMethodSelect";
import EmployeeConsumptionModal from "../../../components/Custom/CashFlow/Consumption/EmployeeConsumptionModal";
import EmployeeConsumptionStatus from "../../../components/Custom/CashFlow/Consumption/EmployeeConsumptionStatus";
import { Columns } from "./Columns";
import MenuConfig from "../../../../constants/MenuConfig";
import Api from "../../../../constants/Api";
import axiosInstance from "../../../../services/AxiosInstance";
import { SearchOperation } from "../../../../utils/ApiUtils";
import { convertDoubleToCurrencyPTBR } from "../../../../utils/ConverterUtils";
import { formatDateToYYYYMMDD } from "../../../../utils/DateUtils";
import { offerToOpenCash } from "../../../components/Custom/CashFlow/Cash/offerToOpenCash";
import "./EmployeeConsumptionList.css";

const formatCurrency = convertDoubleToCurrencyPTBR;

/** Primeiro dia do mês corrente: início padrão do período, o mesmo recorte do extrato. */
const startOfMonth = () => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
};

/**
 * Consumo da equipe: a visão do gestor.
 *
 * O lançamento em si é o mesmo modal da tela de Caixas (EmployeeConsumptionModal). O que só
 * existe aqui é o que muda um lançamento já feito: receber o pagamento de um consumo que estava
 * para descontar e cancelar um lançamento errado. Ficam fora do atalho da recepção de propósito,
 * para quem lança não poder desfazer o próprio lançamento.
 */
const EmployeeConsumptionList = () => {

    const { t } = useTranslation();

    const statusOptions = useMemo(() => [
        { value: EmployeeConsumptionStatus.PENDING, label: t('cash_flow.consumption_status_pending') },
        { value: EmployeeConsumptionStatus.PAID, label: t('cash_flow.consumption_status_paid') },
        { value: EmployeeConsumptionStatus.CANCELED, label: t('cash_flow.consumption_status_canceled') }
    ], [t]);

    const [employeeFilter, setEmployeeFilter] = useState(null);
    const [statusFilter, setStatusFilter] = useState(null);
    // Abre no mês corrente, do dia 1 até hoje, o mesmo recorte que o gestor usa no extrato.
    const [startDateFilter, setStartDateFilter] = useState(startOfMonth);
    const [endDateFilter, setEndDateFilter] = useState(() => new Date());

    /** Traduz os campos da tela para os filtros que a API entende. */
    const buildFilters = useCallback((employee, status, startDate, endDate) => {
        const activeFilters = [];

        if (employee) {
            activeFilters.push({ field: "employee.id", value: employee.value, operation: SearchOperation.EQUAL });
        }
        if (status) {
            activeFilters.push({ field: "consumptionStatus", value: status.value, operation: SearchOperation.EQUAL });
        }
        // Período pela data do consumo, que é a mesma que o extrato usa para descontar.
        if (startDate) {
            activeFilters.push({
                field: "consumptionDate",
                value: formatDateToYYYYMMDD(startDate),
                operation: SearchOperation.GREATER_THAN_EQUAL
            });
        }
        if (endDate) {
            activeFilters.push({
                field: "consumptionDate",
                value: formatDateToYYYYMMDD(endDate),
                operation: SearchOperation.LESS_THAN_EQUAL
            });
        }

        return activeFilters;
    }, []);

    const [filters, setFilters] = useState(() => buildFilters(null, null, startOfMonth(), new Date()));

    const [totals, setTotals] = useState({ pending: 0, paid: 0, count: 0 });
    const [loadingTotals, setLoadingTotals] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    const [showNewModal, setShowNewModal] = useState(false);
    const [showPayModal, setShowPayModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const [cancelReason, setCancelReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Os cards somam os mesmos filtros da grade numa consulta própria: o ListPage pagina, e
    // somar só a página exibida daria um total menor que o real.
    useEffect(() => {
        setLoadingTotals(true);
        axiosInstance.post(`${Api.cash_flow.employee_consumption}/findByFilters`, { filters })
            .then(({ data }) => {
                const rows = data || [];
                const sumBy = (status) => rows
                    .filter(item => item.consumptionStatus === status)
                    .reduce((total, item) => total + Number(item.total || 0), 0);
                setTotals({
                    pending: sumBy(EmployeeConsumptionStatus.PENDING),
                    paid: sumBy(EmployeeConsumptionStatus.PAID),
                    // Cancelado não conta: foi lançamento errado, não consumo.
                    count: rows.filter(item => item.consumptionStatus !== EmployeeConsumptionStatus.CANCELED).length
                });
            })
            .catch(error => ApiError(error))
            .finally(() => setLoadingTotals(false));
    }, [filters]);

    // Uma cópia dos mesmos filtros recarrega grade e cards depois de uma ação, sem mudar o
    // recorte que o gestor escolheu.
    const reload = () => {
        setSelectedRows([]);
        setFilters(current => [...current]);
    };

    const handleSearch = () => {
        setFilters(buildFilters(employeeFilter, statusFilter, startDateFilter, endDateFilter));
    };

    const handleClearFilters = () => {
        const start = startOfMonth();
        const end = new Date();

        setEmployeeFilter(null);
        setStatusFilter(null);
        setStartDateFilter(start);
        setEndDateFilter(end);
        setFilters(buildFilters(null, null, start, end));
    };

    /** Linha escolhida na grade, ou aviso quando não há exatamente uma. */
    const requireSingleRow = () => {
        if (selectedRows.length !== 1) {
            warnAlert(t(selectedRows.length === 0 ? 'validation.required.row' : 'validation.required.row_select_one'));
            return null;
        }
        return selectedRows[0];
    };

    const openPayModal = () => {
        const row = requireSingleRow();
        if (!row) {
            return;
        }
        if (row.consumptionStatus !== EmployeeConsumptionStatus.PENDING) {
            warnAlert(t('cash_flow.consumption_only_pending_can_be_paid'));
            return;
        }
        setPaymentMethod(null);
        setShowPayModal(true);
    };

    const openCancelModal = () => {
        const row = requireSingleRow();
        if (!row) {
            return;
        }
        if (row.consumptionStatus === EmployeeConsumptionStatus.CANCELED) {
            warnAlert(t('cash_flow.consumption_already_canceled'));
            return;
        }
        setCancelReason('');
        setShowCancelModal(true);
    };

    const confirmPayment = () => {
        if (!paymentMethod?.value) {
            warnAlert(t('validation.required.payment_method'));
            return;
        }
        sendPayment(selectedRows[0], paymentMethod.value);
    };

    /** Recebe o pagamento do consumo; sem caixa aberto para dinheiro, oferece abrir. */
    const sendPayment = (consumption, method) => {
        setIsSubmitting(true);
        axiosInstance.patch(`${Api.cash_flow.employee_consumption}/${consumption.id}/pay`, { paymentMethod: method })
            .then(() => {
                setShowPayModal(false);
                successAlert(t('message.operation_successfully'), reload);
            })
            .catch(error => {
                // Mesma saída da venda e do lançamento: abrir o caixa com saldo zero na filial
                // do consumo e receber em seguida. O modal sai da frente para o alerta aparecer
                // e volta se a pessoa desistir.
                const handled = offerToOpenCash({
                    error,
                    branchId: consumption.branch?.id,
                    question: t('cash_flow.open_cash_before_consumption'),
                    openingNote: t('cash_flow.automatic_opening_for_consumption'),
                    onBeforeAsk: () => setShowPayModal(false),
                    onOpened: () => sendPayment(consumption, method),
                    onDeclined: () => setShowPayModal(true)
                });
                if (!handled) {
                    ApiError(error);
                }
            })
            .finally(() => setIsSubmitting(false));
    };

    const confirmCancel = () => {
        if (!cancelReason.trim()) {
            warnAlert(t('cash_flow.consumption_cancel_reason_required'));
            return;
        }
        setIsSubmitting(true);
        axiosInstance.patch(`${Api.cash_flow.employee_consumption}/${selectedRows[0].id}/cancel`,
            { reason: cancelReason.trim() })
            .then(() => {
                setShowCancelModal(false);
                successAlert(t('message.operation_successfully'), reload);
            })
            .catch(error => ApiError(error))
            .finally(() => setIsSubmitting(false));
    };

    const selected = selectedRows[0];

    return (
        <>
            <PageTitle activeMenu={t('cash_flow.employee_consumptions')} motherMenu={t('cash_flow.label')} />

            <EmployeeConsumptionModal show={showNewModal} setShow={setShowNewModal} onSaved={reload} />

            <ModalPageEvent
                show={showPayModal}
                setShow={setShowPayModal}
                centered={true}
                title={t('cash_flow.consumption_receive_payment')}
                btnText={t('confirm')}
                btnOnClick={confirmPayment}
                btnDisabled={isSubmitting}
                child={
                    <Row className="g-3">
                        {selected && (
                            <Col md={12}>
                                <div className="employee-consumption-list__summary">
                                    <span>{selected.employee?.name} · {selected.product?.name}</span>
                                    <strong>{formatCurrency(selected.total)}</strong>
                                </div>
                            </Col>
                        )}
                        <Col md={12}>
                            <FormGroup>
                                <FormLabel>{t('payment_method')} <AsteriskRequired /></FormLabel>
                                <PaymentMethodSelect value={paymentMethod} onChange={setPaymentMethod} hideSubscription />
                                <small className="text-muted d-block mt-1">{t('cash_flow.consumption_pay_now_hint')}</small>
                            </FormGroup>
                        </Col>
                    </Row>
                }
            />

            <ModalPageEvent
                show={showCancelModal}
                setShow={setShowCancelModal}
                centered={true}
                title={t('cash_flow.consumption_cancel')}
                btnText={t('confirm')}
                btnOnClick={confirmCancel}
                btnDisabled={isSubmitting}
                child={
                    <Row className="g-3">
                        {selected && (
                            <Col md={12}>
                                <div className="employee-consumption-list__summary">
                                    <span>{selected.employee?.name} · {selected.product?.name}</span>
                                    <strong>{formatCurrency(selected.total)}</strong>
                                </div>
                                <small className="text-muted d-block mt-2">
                                    {selected.consumptionStatus === EmployeeConsumptionStatus.PAID
                                        ? t('cash_flow.consumption_cancel_paid_hint')
                                        : t('cash_flow.consumption_cancel_hint')}
                                </small>
                            </Col>
                        )}
                        <Col md={12}>
                            <FormGroup>
                                <FormLabel>{t('reason')} <AsteriskRequired /></FormLabel>
                                <InputText
                                    name="cancelReason"
                                    maxLength={255}
                                    value={cancelReason}
                                    onChange={(event) => setCancelReason(event.target.value)}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                }
            />

            <FilterAccordion
                title="filters"
                onSearch={handleSearch}
                onClear={handleClearFilters}
                centerButtons={false}
            >
                {/* Período primeiro, como no resto do sistema. Colunas md para os filtros
                    empilharem no celular. */}
                <Row className="g-2 align-items-end">
                    <Col lg={3} md={6}>
                        <FormLabel>{t('start_date')}</FormLabel>
                        <InputDate selected={startDateFilter} onChange={setStartDateFilter} />
                    </Col>
                    <Col lg={3} md={6}>
                        <FormLabel>{t('end_date')}</FormLabel>
                        <InputDate selected={endDateFilter} onChange={setEndDateFilter} />
                    </Col>
                    <Col lg={3} md={6}>
                        <FormLabel>{t('employee')}</FormLabel>
                        <EmployeeSelect filterProvidesService={false} value={employeeFilter} onChange={setEmployeeFilter} />
                    </Col>
                    <Col lg={3} md={6}>
                        <FormLabel>{t('status')}</FormLabel>
                        <CustomSelect
                            options={statusOptions}
                            value={statusFilter}
                            onChange={setStatusFilter}
                            isClearable
                            placeholder={t('select')}
                        />
                    </Col>
                </Row>
            </FilterAccordion>

            <ListMetricCards>
                <ListMetricCard
                    accent="warning"
                    label={t('cash_flow.consumptions_pending_in_period')}
                    value={formatCurrency(totals.pending)}
                    icon="fas fa-hand-holding-dollar"
                    loading={loadingTotals}
                />
                <ListMetricCard
                    accent="success"
                    label={t('cash_flow.consumptions_paid_in_period')}
                    value={formatCurrency(totals.paid)}
                    icon="fas fa-cash-register"
                    loading={loadingTotals}
                />
                <ListMetricCard
                    accent="info"
                    label={t('cash_flow.consumptions_count_in_period')}
                    value={totals.count}
                    icon="fas fa-mug-hot"
                    loading={loadingTotals}
                />
            </ListMetricCards>

            {/* Sem os botões padrão: não existe editar nem excluir consumo, só cancelar. */}
            <ListPage
                showPageTitle={false}
                activeMenu={t('cash_flow.employee_consumptions')}
                motherMenu={t('cash_flow.label')}
                path={Api.cash_flow.employee_consumption}
                route={MenuConfig.cash_flow.employee_consumption.route}
                columns={Columns}
                filters={filters}
                hideDefaultButtons
                setSelectedRows={setSelectedRows}
                customButtons={
                    <>
                        <Dropdown.Item onClick={() => setShowNewModal(true)}>
                            <SaveIcon /> {t('new')}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={openPayModal}>
                            <i className="fa-solid fa-cash-register me-2" aria-hidden="true" />
                            {t('cash_flow.consumption_receive_payment')}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={openCancelModal}>
                            <i className="fa-solid fa-ban me-2" aria-hidden="true" />
                            {t('cash_flow.consumption_cancel')}
                        </Dropdown.Item>
                    </>
                }
            />
        </>
    );
}

export default EmployeeConsumptionList;
